import React, { Fragment, useState } from 'react';
import { bool, func, string, object, number } from 'prop-types';
import moment from 'moment';
import { sentenceCase } from 'change-case';
import get from 'lodash/get';
import {
  Chip,
  Collapse,
  Dialog,
  IconButton,
  TableCell,
  TableRow,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import clsx from 'clsx';

import EditKey from '../../EditKey';
import SingleKeyView from '../SingleKeyView';
import { API_KEY_STATUS_DELETE_FAILED, API_KEY_STATUS_EXPIRED } from '../../../../../constants';
import {
  KEY_STATUS_ENABLED,
} from '../../labels';

const SingleRow = ({
  row,
  idx,
  classes,
  notifyMessages,
  appSecretHashingMetadata,
  applicationUuid,
  isLocking,
  isApiKeyExpiryEnabled,
  startReportingDays,
  urgentReportingDays,
  locked,
  disabledByInternal,
  expandIndex,
  setExpandIndex,
  onSaveDone,
  deleteKey,
  regenerateSecret,
  regeneratedSecretDetails,
  regerateSecretStatus,
  isAdmin,
  isEditDisabled,
  defaultHashType,
  isPortalAdmin,
  fetchApplicationKeys }) => {

  const [isEditKeyOpen, setIsEditKeyOpen] = useState(false);

  const getKeyStatus = () => {
    const status = get(row, 'status');
    let className = classes.enabled;
    if (status === API_KEY_STATUS_DELETE_FAILED) {
      className = classes.deleteFailed;
    } else if (status === KEY_STATUS_ENABLED) {
      className = classes.enabled;
    } else if (status === API_KEY_STATUS_EXPIRED) {
      className = classes.deleteFailed;
    } else {
      className = classes.disabled;
    }
    return (<span className={clsx(classes.status, className)}>
      {sentenceCase(get(row, 'status'))}
    </span>);
  };

  const getExpiryStatus = () => {
    const secretExpiryTsInMS = get(row, 'secretExpiryTs');
    const status = get(row, 'status');
    const keyId = get(row, 'apiKey');
    if (!secretExpiryTsInMS) {
      return (<span />)
    }
    // const secretExpiryTsInMS = 1682009761000;
    const expiryTime = moment(secretExpiryTsInMS);
    const currentTime = moment();
    const days = expiryTime.diff(currentTime, 'days');
    let className = classes.normalKey;
    let label = '';
    // string to represent status, which is used by automation
    let expiryStatus;
    if (status === 'EXPIRED' || status === 'DISABLED') {
      label = '';
      className = '';
      expiryStatus = 'none';
    } else {
      label = days;
      if (days <= urgentReportingDays) {
        className = classes.urgentReportingKey;
        expiryStatus = 'urgentReporting';
        if (days < 0) {
          label = 0;
        }
      } else if (days > startReportingDays) {
        className = classes.normalKey;
        expiryStatus = 'normal';
      } else {
        className = classes.startReportingKey;
        expiryStatus = 'startReporting';
      }
    }

    return (
    <span
      data-apim-test={`key-${keyId}-expiry-status-${expiryStatus}`}
      className={clsx(classes.status, className)}
    >
      {label}
    </span>);
  };

  return (
    <Fragment>
      <Dialog
        open={isEditKeyOpen}
        onClose={() => setIsEditKeyOpen(false)}
        aria-labelledby="edit-key-dialog"
        id={row.apiKey}
        classes={{
          paper: classes.paperClass,
        }}
      >
        <EditKey
          applicationUuid={applicationUuid}
          details={row}
          closeAction={() => {
            setIsEditKeyOpen(false);
            fetchApplicationKeys(applicationUuid);
          }}
          appSecretHashingMetadata={appSecretHashingMetadata}
          notifyMessages={notifyMessages}
          isLocking={isLocking}
          onSaveDone={onSaveDone}
          isAdmin={isAdmin}
          isPortalAdmin={isPortalAdmin}
        />
      </Dialog>
      <TableRow
        data-apim-test={`${row.apiKey}-single-row`}
        onClick={() => setExpandIndex(expandIndex === idx ? -1 : idx)}
      >
        <TableCell component="th" scope="row">
            {row.name}
          {get(row, 'defaultKey') &&
            <Chip
              className={classes.defaultChip}
              size="small"
              label={'Default'}
            />
          }
        </TableCell>
        <TableCell component="th" scope="row">
          {getKeyStatus()}
        </TableCell>
        {isApiKeyExpiryEnabled && <TableCell component="th" scope="row">
          {getExpiryStatus()}
        </TableCell>}
        <TableCell component="th">
          <div id={`expand-collapse-button-${idx}`}>
            <IconButton
            className={classes.expandCollapseButton}
            size="small"
            onClick={() => setExpandIndex(expandIndex === idx ? -1 : idx)}>
              {expandIndex===idx ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={6}>
          <Collapse in={expandIndex === idx} timeout="auto" unmountOnExit>
            <div className={classes.singleKeyViewCell}>
              <SingleKeyView
                idx={idx}
                isApiKeyExpiryEnabled={isApiKeyExpiryEnabled}
                keyDetails={row}
                classes={classes}
                notifyMessages={notifyMessages}
                setIsEditKeyOpen={setIsEditKeyOpen}
                isLocking={isLocking}
                locked={locked}
                disabledByInternal={disabledByInternal}
                appSecretHashingMetadata={appSecretHashingMetadata}
                onSaveDone={onSaveDone}
                deleteKey={deleteKey}
                regenerateSecret={regenerateSecret}
                applicationUuid={applicationUuid}
                regeneratedSecretDetails={regeneratedSecretDetails}
                regerateSecretStatus={regerateSecretStatus}
                defaultHashType={defaultHashType}
                isEditDisabled={isEditDisabled}
                isPortalAdmin={isPortalAdmin}
                isAdmin={isAdmin}
              />
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

SingleRow.propTypes = {
  row: object,
  idx: number,
  classes: object,
  notifyMessages: func,
  isAdmin: bool,
  appSecretHashingMetadata: object,
  applicationUuid: string,
  isLocking: bool,
  isApiKeyExpiryEnabled: bool,
  startReportingDays: number,
  urgentReportingDays: number,
  locked: bool,
  disabledByInternal: bool,
  expandIndex: number,
  setExpandIndex: func,
  regeneratedSecretDetails: object,
  regerateSecretStatus: string,
  deleteKey: func,
  regenerateSecret: func,
  onSaveDone: func,
  defaultHashType: string,
  isEditDisabled:bool,
  fetchApplicationKeys: func,
  isPortalAdmin: bool,
};

export default SingleRow;